import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchCustomersByOrganization } from "../../api/customers";
import { storage } from "../../helpers/storage";

const initialState = {
  value: {
    customers: [],
    lastPage: 1,
    currentPage: 1,
    limit: process.env.REACT_APP_PAGE_LIMIT || 30,
    loading: false,
    loadingNextPage: false,
    loadingBackground: false,
    loadBackgroundFailed: false,
    lastParams: {
      organization_id: null,
      keyword: "",
    },
    error: null,
  },
};

export const loadCustomers = createAsyncThunk(
  "customers/loadCustomers",
  async ({ organization_id, keyword }, { getState, rejectWithValue }) => {
    try {
      const state = getState().customerReducer;

      const limit = state.value.limit;

      const response = await fetchCustomersByOrganization(
        organization_id,
        keyword,
        1,
        limit
      );

      return {
        customers: response.data?.customers || [],
        lastPage: response.data?.lastPage || 1,
        newParams: {
          organization_id: organization_id,
          keyword: keyword,
        },
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch customers."
      );
    }
  }
);

export const reloadCustomersInBackground = createAsyncThunk(
  "customers/reloadCustomersInBackground",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().customerReducer;

      const {
        lastParams: { organization_id, keyword },
        currentPage,
        limit,
        lastPage,
      } = state.value;

      const totalLimit = currentPage > 3 ? limit * 3 : currentPage * limit;
      const finalCurrentPage = currentPage > 3 ? 3 : currentPage;

      const response = await fetchCustomersByOrganization(
        organization_id,
        keyword,
        1,
        totalLimit
      );

      return {
        customers: response.data?.customers || [],
        lastPage: lastPage,
        currentPage: finalCurrentPage,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch background customers."
      );
    }
  }
);

export const loadCustomersIfNeeded = createAsyncThunk(
  "customers/loadCustomersIfNeeded",
  async ({ organization_id, keyword }, { getState, dispatch }) => {
    const state = getState().customerReducer;

    const currentParams = { organization_id, keyword };
    const previousParams = {
      organization_id: state.value.lastParams?.organization_id,
      keyword: state.value.lastParams?.keyword,
    };

    let storageCustomers = await storage.get("customers");
    storageCustomers = storageCustomers ? JSON.parse(storageCustomers) : null;

    if (storageCustomers && storageCustomers?.length) {
      dispatch(updateCustomers({ customers: storageCustomers }));
      dispatch(updateCustomerLoading(false));
    } else if (
      JSON.stringify(currentParams) !== JSON.stringify(previousParams)
    ) {
      dispatch(updateCustomerLoading(true));
    }

    if (JSON.stringify(currentParams) === JSON.stringify(previousParams)) {
      return dispatch(reloadCustomersInBackground());
    }

    return dispatch(loadCustomers({ organization_id, keyword }));
  }
);

export const loadNextCustomers = createAsyncThunk(
  "customers/loadNextCustomers",
  async ({ page }, { getState, rejectWithValue }) => {
    try {
      const state = getState().customerReducer;

      const limit = state.value.limit;
      const keyword = state.value.lastParams?.keyword;
      const organization_id = state.value.lastParams?.organization_id;

      if (state.value.currentPage === page) {
        return {
          customers: [],
          lastPage: state.value.lastPage,
          currentPage: page,
        };
      } else {
        const response = await fetchCustomersByOrganization(
          organization_id,
          keyword,
          page,
          limit
        );

        return {
          customers: response.data?.customers || [],
          lastPage: response.data?.lastPage,
          currentPage: page,
        };
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch customers."
      );
    }
  }
);

const customer = createSlice({
  name: "customers",
  initialState,
  reducers: {
    updateCustomers: (state, data) => {
      state.value.customers = data.payload.customers;
    },
    addNewCustomer: (state, data) => {
      const { customer } = data.payload;

      state.value.customers?.unshift(customer);
      storage.set("customers", JSON.stringify(state.value.customers));
    },
    updateExistingCustomer: (state, data) => {
      const { customer } = data.payload;
      const exists = state.value.customers?.findIndex(
        (el) => el?.id === customer?.id
      );

      if (exists !== -1) {
        state.value.customers[exists] = customer;
      }

      storage.set("customers", JSON.stringify(state.value.customers));
    },
    updateCustomerLoading: (state, data) => {
      state.value.loading = data.payload;
      state.value.error = null;
    },
    deleteExistingCustomer: (state, data) => {
      const { customer } = data.payload;
      const exists = state.value.customers?.findIndex(
        (el) => el?.id === customer?.id
      );

      if (exists !== -1) {
        state.value.customers?.splice(exists, 1);
      }

      storage.set("customers", JSON.stringify(state.value.customers));
    },
    clearCustomers: () => {
      return {
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCustomers.pending, (state) => {})
      .addCase(loadCustomers.fulfilled, (state, action) => {
        state.value.loading = false;
        state.value.customers = action.payload.customers;
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = 1;
        state.value.lastParams = action.payload.newParams;

        storage.set("customers", JSON.stringify(action.payload.customers));
      })
      .addCase(loadCustomers.rejected, (state, action) => {
        state.value.loading = false;
        state.value.error = action.payload;
        state.value.lastPage = state.value.currentPage;
      });

    builder
      .addCase(loadNextCustomers.pending, (state) => {
        state.value.loadingNextPage = true;
        state.value.error = null;
      })
      .addCase(loadNextCustomers.fulfilled, (state, action) => {
        state.value.loadingNextPage = false;
        state.value.customers = [
          ...state.value.customers,
          ...action.payload.customers,
        ];
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = action.payload.currentPage;

        storage.set(
          "customers",
          JSON.stringify([
            ...state.value.customers,
            ...action.payload.customers,
          ])
        );
      })
      .addCase(loadNextCustomers.rejected, (state, action) => {
        state.value.loadingNextPage = false;
        state.value.error = action.payload;
        state.value.lastPage = state.value.currentPage;
      });

    builder
      .addCase(reloadCustomersInBackground.pending, (state) => {
        state.value.loadingBackground = true;
        state.value.loadBackgroundFailed = false;
      })
      .addCase(reloadCustomersInBackground.fulfilled, (state, action) => {
        state.value.loadingBackground = false;
        state.value.customers = action.payload.customers;
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = action.payload.currentPage;

        storage.set("customers", JSON.stringify(action.payload.customers));
      })
      .addCase(reloadCustomersInBackground.rejected, (state, action) => {
        state.value.loadingBackground = false;
        state.value.error = action.payload;
        state.value.loadBackgroundFailed = true;
        state.value.lastPage = state.value.currentPage;
      });
  },
});

export const {
  updateCustomers,
  addNewCustomer,
  updateExistingCustomer,
  updateCustomerLoading,
  deleteExistingCustomer,
  clearCustomers,
} = customer.actions;

export default customer.reducer;
